import type { CommonLogger } from "./interfaces.js";
import {
  type Account,
  type ApeContext,
  type Environment,
  EnvironmentType,
  type Project,
} from "./interfaces.portal.js";
import type { FileSystemPort } from "./ports.js";

/**
 * Local-specific configuration that is provided to the React app
 *
 * IMPORTANT: This must be kept in sync with the LocalEditorConfig interface
 * in packages/editor/src/utils/local-config.ts
 */
export interface LocalEditorConfig {
  /*
   * This is the url that the user is redirected to when they open the / path
   */
  defaultPath: string;
  apeContext: ApeContext;
}

function npmNameToUrlSafe(name: string): string {
  // Scoped packages: @scope/pkg -> scope-pkg
  if (name.startsWith("@")) {
    return name.slice(1).replace("/", "-");
  }
  // Other names: just URI encode to be safe
  return encodeURIComponent(name.toLowerCase());
}

type PackageJson = { version?: string; name?: string };

/**
 * Human-readable names shown in the portal's account and environment chrome.
 *
 * The preview Worker overrides these so a hosted preview identifies itself as
 * one — with its branch and commit — instead of claiming to be somebody's local
 * machine. That reuses UI the portal already renders, so nothing preview-only
 * has to exist in the frontend.
 */
export interface LocalEditorLabels {
  account?: string;
  environment?: string;
}

export async function getLocalEditorConfig({
  deploymentUrl,
  fs,
  logger,
  labels,
}: {
  deploymentUrl: string;
  fs: FileSystemPort;
  logger: CommonLogger;
  labels?: LocalEditorLabels;
}): Promise<LocalEditorConfig> {
  let pkgJson: PackageJson | undefined;
  try {
    const { content } = await fs.readFileWithStat("package.json");
    pkgJson = JSON.parse(content) as PackageJson;
  } catch (err) {
    logger.warn({ err }, "Failed to load package.json");
  }

  const projectName = npmNameToUrlSafe(pkgJson?.name ?? "project");

  const account: Account = {
    id: "dev",
    name: "dev",
    label: labels?.account ?? "Local Development",
    createdOn: new Date().toISOString(),
    isSharingEnabled: false,
    isMfaEnforced: false,
    isSsoAutoJoinEnabled: false,
    requireSsoAuth: false,
    plan: "FREE_V1",
    supportPin: undefined,
    logoUrl: null,
    entitlements: {
      customDomains: 0,
      aliasDomains: false,
      devPortalZuploBranding: true,
      egressGbPerMonth: 100,
      kvStoreEnabled: true,
      numberOfApiKeys: 100,
      numberOfProjects: 10,
      numberOfUsers: 5,
      requestsPerMonth: 1000000,
      advancedAnalyticsEnabled: true,
      analyticsHistoryDays: 1,
      logHistoryMinutes: 60,
      premiumSupport: false,
      emergencyPhoneSupport: false,
      rbacEnabled: false,
      mfaEnforcement: false,
      enterpriseSingleSignOn: false,
      auditLogsEnabled: false,
    },
    customDomainsInUse: 0,
    tenantId: null,
  };

  const project: Project = {
    sourceControlProvider: "GITHUB",
    accountName: account.name,
    name: projectName,
    productType: "standard",
    createdOn: new Date().toISOString(),
    sourceRepoUrl: null,
    defaultBranch: null,
    hasWorkingCopy: true,
    hasCurrentUserWorkingCopy: true,
    deployedEnvironmentCount: 0,
    isProjectMember: true,
    id: "local-project-id",
    type: "managed-edge",
  };

  const environmentName = `local-${projectName}`;

  const environment: Environment = {
    name: environmentName,
    label: labels?.environment ?? "local",
    urlParameter: environmentName,
    createdOn: Date.now(),
    accountName: account.name,
    projectName: project.name,
    state: "STARTED",
    message: null,
    branchName: null,
    environmentType: EnvironmentType.WORKING_COPY,
    customDomain: undefined,
    updatedOn: Date.now(),
    deploymentVersion: {
      id: "local",
      createdOn: new Date().toISOString(),
      repoUrl: "",
      branch: "",
      sha: "",
      url: deploymentUrl,
      stages: [
        {
          id: "local",
          status: "success",
          stage: "api",
          url: deploymentUrl,
        },
      ],
    },
  };

  const apeContext: ApeContext = {
    account,
    project,
    environment,
  };

  // Build the default path using the same names as the context
  const defaultPath = `/${account.name}/${project.name}/${environment.name}`;

  const localEditorConfig: LocalEditorConfig = { apeContext, defaultPath };

  return localEditorConfig;
}
